import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle2, KeyRound, Lock, Mail } from 'lucide-react';
import { Toaster, toast } from 'sonner';

export const AUTH_FORGOT_PASSWORD_PATH = '/auth/forgot-password';
export const AUTH_RESET_PASSWORD_PATH = '/auth/reset-password';

const authScreenStyles = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: '32px 18px',
  background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF4FF 52%, #FFF7ED 100%)',
  fontFamily: 'Manrope, sans-serif',
};

const authCardStyles = {
  width: '100%',
  maxWidth: '460px',
  background: 'rgba(255, 255, 255, 0.94)',
  border: '1px solid rgba(226, 232, 240, 0.78)',
  borderRadius: '16px',
  padding: '32px',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.02), 0 4px 8px rgba(15, 23, 42, 0.03), 0 12px 24px rgba(15, 23, 42, 0.05)',
};

const inputStyles = {
  width: '100%',
  border: '1px solid #E2E8F0',
  borderRadius: '12px',
  padding: '10px 12px',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryButtonStyles = {
  border: 'none',
  borderRadius: '12px',
  padding: '13px 12px',
  background: 'linear-gradient(90deg, #122A5C 0%, #1F3F7D 72%, #E07000 160%)',
  color: '#FFFFFF',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '15px',
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 12px 24px rgba(18, 42, 92, 0.16)',
};

const secondaryButtonStyles = {
  border: '1px solid #CBD5E1',
  borderRadius: '12px',
  padding: '11px 12px',
  background: '#FFFFFF',
  color: '#334155',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
};

function getUrlParams() {
  if (typeof window === 'undefined') {
    return { searchParams: new URLSearchParams(), hashParams: new URLSearchParams(), path: '/' };
  }

  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const searchParams = new URLSearchParams(window.location.search || '');
  const hashParams = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));

  return { searchParams, hashParams, path };
}

export function getPasswordAuthRoute() {
  const { searchParams, hashParams, path } = getUrlParams();

  if (path === AUTH_FORGOT_PASSWORD_PATH) {
    return 'forgot';
  }

  if (
    path === AUTH_RESET_PASSWORD_PATH
    || searchParams.get('type') === 'recovery'
    || hashParams.get('type') === 'recovery'
    || searchParams.has('code')
    || searchParams.has('error_code')
    || hashParams.has('error_code')
  ) {
    return 'reset';
  }

  return null;
}

function getResetRedirectUrl() {
  if (typeof window === 'undefined') {
    return AUTH_RESET_PASSWORD_PATH;
  }

  return `${window.location.origin}${AUTH_RESET_PASSWORD_PATH}`;
}

function replaceUrl(path) {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState({}, '', path);
}

function AuthFlowShell({ icon: Icon, title, subtitle, children }) {
  return (
    <div style={authScreenStyles}>
      <Toaster position="top-right" richColors />
      <div style={authCardStyles}>
        <div style={{ textAlign: 'center', display: 'grid', justifyItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <img
            src="/enpower-logo-transparent.png"
            alt="EN:POWER"
            style={{ width: '210px', maxWidth: '82%', height: 'auto', display: 'block', marginBottom: '8px' }}
          />
          <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: '#FFF7ED', color: '#E07000', display: 'grid', placeItems: 'center' }}>
            <Icon size={20} />
          </div>
          <h1 style={{ margin: 0, fontFamily: 'Outfit, Manrope, sans-serif', color: '#122A5C', fontSize: '24px', lineHeight: 1.18, fontWeight: 700, letterSpacing: '-0.04em' }}>
            {title}
          </h1>
          {subtitle ? (
            <p style={{ margin: 0, maxWidth: '330px', color: '#64748B', fontSize: '14px', lineHeight: 1.45 }}>
              {subtitle}
            </p>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function PasswordResetFlow({ supabase, initialMode = 'forgot' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [resetReady, setResetReady] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState('');

  const resetRedirectUrl = useMemo(() => getResetRedirectUrl(), []);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const { searchParams, hashParams } = getUrlParams();
    const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
    const errorCode = searchParams.get('error_code') || hashParams.get('error_code');

    if (errorDescription || errorCode) {
      setResetError(errorDescription || `Supabase Fehler: ${errorCode}`);
    }
  }, []);

  useEffect(() => {
    if (!supabase || mode !== 'reset') {
      return undefined;
    }

    let cancelled = false;

    async function prepareRecoverySession() {
      setResetError('');
      try {
        const { searchParams, hashParams } = getUrlParams();
        const code = searchParams.get('code');
        const hasRecoveryToken = Boolean(
          code
          || searchParams.get('type') === 'recovery'
          || hashParams.get('type') === 'recovery'
          || hashParams.has('access_token')
          || hashParams.has('refresh_token')
        );

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            throw error;
          }
          replaceUrl(AUTH_RESET_PASSWORD_PATH);
        }

        const { data, error } = await supabase.auth.getSession();
        if (error) {
          throw error;
        }

        if (!cancelled) {
          const hasSession = Boolean(data.session);
          setResetReady(hasSession);
          if (!hasSession && !hasRecoveryToken) {
            setResetError('Der Reset-Link fehlt, ist abgelaufen oder wurde bereits verwendet.');
          }
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setResetReady(false);
          setResetError(error?.message || 'Reset-Link konnte nicht geprüft werden.');
        }
      }
    }

    prepareRecoverySession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === 'PASSWORD_RECOVERY' || currentSession) {
        setResetReady(Boolean(currentSession));
        setResetError('');
      }
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [mode, supabase]);

  const goToLogin = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  }, []);

  const goToForgot = useCallback(() => {
    setMode('forgot');
    replaceUrl(AUTH_FORGOT_PASSWORD_PATH);
  }, []);

  const handleSendResetMail = useCallback(async (event) => {
    event?.preventDefault();

    if (!supabase) {
      toast.error('Supabase ist nicht konfiguriert.');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      toast.error('Bitte eine E-Mail-Adresse eingeben.');
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: resetRedirectUrl,
      });

      if (error) {
        throw error;
      }

      setMailSent(true);
      toast.success('Wenn ein Account existiert, wurde eine Reset-Mail versendet.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Reset-Mail konnte nicht versendet werden.');
    } finally {
      setBusy(false);
    }
  }, [email, resetRedirectUrl, supabase]);

  const handleUpdatePassword = useCallback(async (event) => {
    event?.preventDefault();

    if (!supabase) {
      toast.error('Supabase ist nicht konfiguriert.');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein.');
      return;
    }

    setBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        throw new Error('Der Reset-Link ist abgelaufen oder wurde bereits verwendet.');
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        throw error;
      }

      setResetDone(true);
      setNewPassword('');
      setConfirmPassword('');
      await supabase.auth.signOut();
      toast.success('Passwort wurde aktualisiert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Passwort konnte nicht geändert werden.');
    } finally {
      setBusy(false);
    }
  }, [confirmPassword, newPassword, supabase]);

  if (mode === 'forgot') {
    return (
      <AuthFlowShell icon={Mail} title="Passwort vergessen" subtitle="Geben Sie die E-Mail-Adresse Ihres Supabase-Accounts ein. Der Link funktioniert zentral für alle Tools dieses Projekts.">
        {mailSent ? (
          <div style={{ display: 'grid', gap: '14px' }}>
            <div style={{ border: '1px solid #BBF7D0', background: '#F0FDF4', color: '#166534', borderRadius: '12px', padding: '14px', display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: 1.45 }}>
              <CheckCircle2 size={18} style={{ flex: '0 0 auto', marginTop: '1px' }} />
              <span>Falls die E-Mail-Adresse existiert, wurde eine Nachricht mit Reset-Link verschickt.</span>
            </div>
            <button type="button" onClick={goToLogin} style={primaryButtonStyles}>Zurück zum Login</button>
          </div>
        ) : (
          <form onSubmit={handleSendResetMail} style={{ display: 'grid', gap: '14px' }}>
            <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
              E-Mail
              <input
                type="email"
                placeholder="name@firma.de"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                style={inputStyles}
              />
            </label>
            <button type="submit" disabled={busy || !email.trim()} style={{ ...primaryButtonStyles, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.78 : 1 }}>
              {busy ? 'E-Mail wird versendet...' : 'Reset-Link senden'}
            </button>
            <button type="button" onClick={goToLogin} style={{ ...secondaryButtonStyles, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
              <ArrowLeft size={15} /> Zum Login
            </button>
          </form>
        )}
      </AuthFlowShell>
    );
  }

  if (resetDone) {
    return (
      <AuthFlowShell icon={CheckCircle2} title="Passwort aktualisiert" subtitle="Sie können sich jetzt mit dem neuen Passwort in allen Tools anmelden, die dieses Supabase-Projekt verwenden.">
        <button type="button" onClick={goToLogin} style={primaryButtonStyles}>Zum Login</button>
      </AuthFlowShell>
    );
  }

  return (
    <AuthFlowShell icon={KeyRound} title="Neues Passwort setzen" subtitle="Legen Sie ein neues Passwort für Ihren Supabase-Account fest.">
      {resetError ? (
        <div style={{ display: 'grid', gap: '14px' }}>
          <div style={{ border: '1px solid #FED7AA', background: '#FFFBF5', color: '#92400E', borderRadius: '12px', padding: '14px', display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: 1.45 }}>
            <Lock size={18} style={{ flex: '0 0 auto', marginTop: '1px' }} />
            <span>{resetError}</span>
          </div>
          <button type="button" onClick={goToForgot} style={primaryButtonStyles}>Neuen Reset-Link anfordern</button>
        </div>
      ) : (
        <form onSubmit={handleUpdatePassword} style={{ display: 'grid', gap: '14px' }}>
          {!resetReady ? (
            <div style={{ border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#475569', borderRadius: '12px', padding: '14px', fontSize: '13px', lineHeight: 1.45 }}>
              Reset-Link wird geprüft...
            </div>
          ) : null}
          <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
            Neues Passwort
            <input
              type="password"
              placeholder="Mindestens 8 Zeichen"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              style={inputStyles}
              disabled={!resetReady || busy}
            />
          </label>
          <label style={{ display: 'grid', gap: '6px', color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
            Neues Passwort bestätigen
            <input
              type="password"
              placeholder="Passwort wiederholen"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              style={inputStyles}
              disabled={!resetReady || busy}
            />
          </label>
          <button
            type="submit"
            disabled={!resetReady || busy || !newPassword || !confirmPassword}
            style={{ ...primaryButtonStyles, cursor: busy ? 'wait' : 'pointer', opacity: (!resetReady || busy) ? 0.78 : 1 }}
          >
            {busy ? 'Passwort wird gespeichert...' : 'Passwort speichern'}
          </button>
          <button type="button" onClick={goToForgot} style={secondaryButtonStyles}>Neuen Link anfordern</button>
        </form>
      )}
    </AuthFlowShell>
  );
}
